import React from 'react';
import { MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

/**
 * DownloadPracticalEvidence
 * Renders a MenuItem that downloads a practical evidence file using RTK Query.
 *
 * Props:
 * - evidenceId: string | number (required)
 * - fileName?: string (optional suggested filename)
 * - onDownloaded?: () => void
 */
export default function DownloadPracticalEvidence({ evidenceId, fileName, onDownloaded }) {
  const [loading, setLoading] = React.useState(false);
  const [hadError, setHadError] = React.useState(false);

  const resolveBase = React.useCallback(() => {
    let envBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_BACKEND_URL)
      || process.env.REACT_APP_BACKEND_URL
      || '';
    envBase = (envBase || '').trim();
    // If not set, default to current origin
    if (!envBase) return window.location.origin;
    try {
      // Support absolute or relative values
      const url = new URL(envBase, window.location.origin);
      const origin = url.origin;
      const path = (url.pathname || '').replace(/\/$/, '');
      return `${origin}${path}`;
    } catch {
      return window.location.origin;
    }
  }, []);

  const handleClick = async () => {
    if (!evidenceId || loading) return;
    setLoading(true);
    setHadError(false);
    try {
      const base = resolveBase();
      const href = `${base}/skills-management/practical-evidences/${evidenceId}/download`;
      // Pull token like baseQuery
      let token;
      try {
        const stored = localStorage.getItem('user');
        if (stored) token = JSON.parse(stored)?.token;
      } catch (_) {}

      const res = await fetch(href, {
        method: 'GET',
        headers: {
          Accept: 'application/octet-stream,application/pdf,image/*,video/*',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        cache: 'no-cache',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Try to use filename from Content-Disposition
      const dispo = res.headers.get('content-disposition') || '';
      let suggested = fileName || `evidence-${evidenceId}`;
      const match = dispo.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (match) suggested = decodeURIComponent(match[1] || match[2] || suggested);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggested;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (onDownloaded) onDownloaded();
    } catch (err) {
      setHadError(true);
      // fallback: open in new tab (may still download)
      const base = resolveBase();
      const href = `${base}/skills-management/practical-evidences/${evidenceId}/download`;
      window.open(href, '_blank', 'noopener');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MenuItem onClick={handleClick} disabled={!evidenceId || loading}>
      <ListItemIcon>
        {loading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
      </ListItemIcon>
      <ListItemText primary={hadError ? 'Retry Download' : 'Download'} />
    </MenuItem>
  );
}

